/**
 * api-client — fetch wrapper with cookie-based auth + typed errors.
 *
 * Authorization (post M2):
 *   The browser holds httpOnly `xcrmiq_access` (15 min) + `xcrmiq_refresh` (5 h)
 *   cookies set by /api/v1/auth/login. We send `credentials: 'include'` on
 *   every request so the cookies travel automatically.
 *
 *   On 401, we transparently call /api/v1/auth/refresh once and replay the
 *   request (including `/auth/me` — otherwise an expired access cookie would
 *   look like a full logout). If refresh fails, the caller sees the original 401.
 *
 *   Legacy bearer support: if `setStoredToken` was used (PoW carry-over),
 *   the token is forwarded as `Authorization: Bearer …`. New code should
 *   not use this path; it stays for backward compatibility with code paths
 *   not yet migrated to cookies.
 *
 * Errors:
 *   Non-2xx responses throw an `ApiError`. Network errors and timeouts also
 *   throw `ApiError` so callers (React Query) see a consistent shape.
 */

import { isPlanGateCode, reportPlanGate } from './plan-gate'

const LEGACY_TOKEN_STORAGE_KEY = 'xcrmiq.token'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  /**
   * G-38c — the shell already showed this 402 as a plan-gate toast, so a
   * caller's own error toast would only repeat it (see lib/plan-gate.ts).
   */
  planGateReported = false

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

/** True when `err` is a 402 the shell already toasted — skip your own error toast. */
export function isPlanGateReported(err: unknown): boolean {
  return err instanceof ApiError && err.planGateReported
}

const DEFAULT_BASE = '/api/v1'
// console.log("DEFAULT_BASE", import.meta.env.VITE_API_BASE_URL);

const DEFAULT_TIMEOUT_MS = 8_000

function readLegacyToken(): string | null {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(LEGACY_TOKEN_STORAGE_KEY)
      : null
  } catch {
    return null
  }
}

/**
 * Legacy carry-over from the localStorage era. Setting `null` clears.
 * Modern login uses cookies — this is only kept so non-cookie callers
 * (mobile, server-to-server) can attach a bearer when they have one.
 */
export function setStoredToken(token: string | null): void {
  try {
    if (typeof window === 'undefined') return
    if (token === null) window.localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY)
    else window.localStorage.setItem(LEGACY_TOKEN_STORAGE_KEY, token)
  } catch {
    // ignore
  }
}

export function getStoredToken(): string | null {
  return readLegacyToken()
}

/** Best-effort logout helper that clears any legacy token + invalidates cookies. */
export async function clearAuth(): Promise<void> {
  setStoredToken(null)
  try {
    await fetch(`${DEFAULT_BASE}/auth/logout`, { method: 'POST', credentials: 'include' })
  } catch {
    // ignore
  }
}

type FetchOptions = Omit<RequestInit, 'body' | 'signal'> & {
  body?: unknown
  baseUrl?: string
  timeoutMs?: number
  signal?: AbortSignal
  /** Bypass the auth header (used by /auth/login). */
  skipAuth?: boolean
  /**
   * 402 plan-gate codes this caller explains inline (see lib/plan-gate.ts);
   * the shell's global "View plans" toast is skipped for them.
   */
  handledPlanGateCodes?: ReadonlyArray<string>
}

/**
 * Optional hook invoked when a request 401s AND the silent refresh fails —
 * i.e. the session is dead. `useAuth` registers `invalidateAuthCache` here so
 * the app re-validates `/auth/me` (correcting a transient blip, or flipping to
 * logged-out and bouncing to login) instead of surfacing a generic
 * "failed to load" on every data hook. Registered via a setter to avoid a
 * circular import (useAuth already imports this module).
 */
let _onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  _onUnauthorized = fn
}

let _refreshInFlight: Promise<boolean> | null = null

/**
 * Refresh the access cookie. Coalesced so concurrent 401s don't cause
 * a thundering herd of refresh calls.
 *
 * Exported (M-COP W1) so the copilot SSE client — which bypasses apiFetch
 * because of the 8s timeout and JSON-only contract — reuses the SAME
 * coalesced refresh on its one 401 retry instead of racing a second one.
 */
export async function refreshAccess(): Promise<boolean> {
  if (_refreshInFlight) return _refreshInFlight
  _refreshInFlight = (async () => {
    try {
      const res = await fetch(`${DEFAULT_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      return res.ok
    } catch {
      return false
    } finally {
      _refreshInFlight = null
    }
  })()
  return _refreshInFlight
}

async function performFetch(
  path: string,
  baseUrl: string,
  opts: FetchOptions,
): Promise<Response> {
  const headers = new Headers(opts.headers ?? undefined)
  if (!headers.has('Content-Type') && opts.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }
  if (!opts.skipAuth) {
    const legacy = readLegacyToken()
    if (legacy && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${legacy}`)
  }

  const ctrl = new AbortController()
  if (opts.signal) {
    if (opts.signal.aborted) ctrl.abort(opts.signal.reason)
    else
      opts.signal.addEventListener('abort', () => ctrl.abort(opts.signal?.reason), {
        once: true,
      })
  }
  const timeoutId = setTimeout(
    () => ctrl.abort(new ApiError(0, 'TIMEOUT', `Request to ${path} timed out`)),
    opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  )

  try {
    return await fetch(`${baseUrl}${path}`, {
      ...opts,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: ctrl.signal,
      // Always include cookies — that's how httpOnly access/refresh travel.
      credentials: opts.credentials ?? 'include',
    })
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Compile-time guard against unwrapping the envelope twice.
 *
 * `apiFetch` returns the envelope's `data`, never the envelope itself. Asking
 * it for an envelope-shaped type and then reading `.data` off the result
 * unwraps twice: it typechecks perfectly and evaluates to `undefined` at
 * runtime, so the feature goes quietly inert while tests and `tsc` stay green.
 *
 * That exact mistake shipped three times — lead import, WhatsApp onboarding
 * and Twilio WhatsApp — each behind a flag, each undetectable without a
 * browser. Rather than fix three instances, resolve an envelope-shaped request
 * to a hint string, which makes every future occurrence a compile error at the
 * call site that says what to do instead. Per development-policy §5 (fix the
 * class) and §7 (prefer "cannot happen" to "is checked").
 */
export type Payload<T> = T extends { ok: unknown; data: unknown }
  ? 'apiFetch already unwraps { ok, data } — ask for the payload type, not the envelope'
  : T

export async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<Payload<T>> {
  const response = await fetchWithRefresh(path, opts)

  if (response.status === 204) return undefined as Payload<T>

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    // express-rate-limit / nginx HTML bodies used to surface as a useless
    // "Non-JSON response from /auth/login". Prefer a status-aware message.
    const code = response.status === 429 ? 'RATE_LIMITED' : 'INVALID_JSON'
    const message =
      response.status === 429
        ? 'Too many requests. Please wait a few minutes and try again.'
        : `Non-JSON response from ${path} (HTTP ${response.status})`
    throw new ApiError(response.status, code, message)
  }

  if (!response.ok) {
    const errEnvelope = payload as { error?: { code?: string; message?: string; details?: unknown } }
    const code = errEnvelope.error?.code ?? `HTTP_${response.status}`
    const message = errEnvelope.error?.message ?? response.statusText
    const err = new ApiError(response.status, code, message, errEnvelope.error?.details)
    if (response.status === 402 && isPlanGateCode(code) && !opts.handledPlanGateCodes?.includes(code)) {
      err.planGateReported = reportPlanGate({ code, message, details: errEnvelope.error?.details })
    }
    throw err
  }

  const envelope = payload as { ok?: boolean; data?: unknown }
  if (envelope && envelope.ok === true && 'data' in envelope) {
    return envelope.data as Payload<T>
  }
  return payload as Payload<T>
}

/**
 * The raw `Response` for `path`, with the same auth handling as `apiFetch` (cookies, legacy
 * bearer, one transparent refresh-and-retry on 401). For non-JSON bodies such as file downloads;
 * the caller checks `response.ok` and reads the body itself.
 */
export async function fetchWithRefresh(path: string, opts: FetchOptions = {}): Promise<Response> {
  const baseUrl =
    opts.baseUrl ?? (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? DEFAULT_BASE

  let response: Response
  try {
    response = await performFetch(path, baseUrl, opts)
  } catch (err) {
    if (err instanceof ApiError) throw err
    throw new ApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network failure')
  }

  // Transparent refresh-and-retry on 401. Skip only the refresh call itself
  // (and callers that set skipAuth — login/register/forgot/etc.) so `/auth/me`
  // can recover from an expired 15-min access cookie while the 5h session lives.
  const barePath = (path.split('?')[0] ?? path).replace(/\/$/, '') || path
  const isRefreshCall = barePath === '/auth/refresh'
  if (response.status === 401 && !opts.skipAuth && !isRefreshCall) {
    const refreshed = await refreshAccess()
    if (refreshed) {
      try {
        response = await performFetch(path, baseUrl, opts)
      } catch (err) {
        if (err instanceof ApiError) throw err
        throw new ApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network failure')
      }
    } else if (barePath !== '/auth/me') {
      // Refresh failed → the session is (probably) dead. Ask the app to
      // re-validate auth: a transient blip self-corrects via /auth/me, a real
      // expiry flips to logged-out + redirects to login. This turns a stack of
      // generic "failed to load" cards into a clean re-auth prompt. The
      // original 401 still throws below so the in-flight caller settles.
      //
      // NEVER for `/auth/me` itself: /auth/me IS the re-validation call, so
      // firing the handler here recurses — the handler re-polls /auth/me, which
      // 401s, refresh fails, handler fires again — hammering /auth/me +
      // /auth/refresh and remounting the whole app in a tight loop (the logged-
      // out landing page flickers as isLoading toggles). The /auth/me caller
      // (loadMe) already treats a 401 as "logged out", so no re-validation is
      // needed or wanted on this path.
      try {
        _onUnauthorized?.()
      } catch {
        /* never let the handler break the response path */
      }
    }
  }
  return response
}
